import { useCallback, useMemo, useRef, useState } from "react";

import {
  faChevronDown,
  faFiles,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { times } from "lodash-es";
import Dropzone, { DropzoneRef, FileRejection } from "react-dropzone";

import { useDownloadFiles } from "src/api/filesApi/queries";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { type OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  FileCategory,
  type FileCategoryData,
} from "src/cases/caseView/FileCategory";
import {
  FILE_OPERATIONS,
  useFileCategoryOperation,
} from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import {
  MAX_CASE_FILES,
  useCaseFilesUpload,
} from "src/cases/useCaseFilesUpload";
import { GetFilesEnvFilesGetEnvEnum } from "src/clients/files";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { useCapabilities } from "src/hooks/useCapabilities";
import { type Environment } from "src/hooks/useEnvironment";
import { useCurrentUserId } from "src/store/AuthStore";
import {
  CASE_FILE_ACCEPTED_FORMATS,
  DEFAULT_ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

type CaseFilesProps = {
  caseData?: CaseDeserialized;
  isLoading: boolean;
};

export const CaseFiles: React.FC<CaseFilesProps> = ({
  caseData,
  isLoading,
}) => {
  const fileCategoryOperation = useFileCategoryOperation();
  const downloadFiles = useDownloadFiles();
  const currentUserId = useCurrentUserId();
  const { caseFiles, cases } = useCapabilities();
  const dropzoneRef = useRef<DropzoneRef>(null);

  const [downloadTarget, setDownloadTarget] = useState<string | null>(null);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [newlyUploadedFileIds, setNewlyUploadedFileIds] = useState<string[]>(
    [],
  );

  const canEditFiles = !!(
    caseData?.data &&
    currentUserId &&
    (cases.canManage ||
      (caseFiles.canEdit && caseData.data.assignee?.id === currentUserId))
  );

  const fileCategories = useMemo((): FileCategoryData[] => {
    if (!caseData?.data.file_categories) return [];

    return caseData.data.file_categories.map((category) => ({
      id: category.id,
      display_name: category.display_name,
      description: category.description || "",
      category_key: category.category_key,
      file_ids: category.file_ids || [],
    }));
  }, [caseData?.data.file_categories]);

  const currentFileIds = useMemo(
    () =>
      Object.fromEntries(
        fileCategories.map((cat) => [cat.category_key, cat.file_ids]),
      ),
    [fileCategories],
  );

  const handleCategorySelect = useCallback((categoryKey: string) => {
    setPendingCategory(categoryKey);
    dropzoneRef.current?.open();
  }, []);

  const dropdownElements = useMemo(
    (): OptionsDropdownElement[] => [
      {
        key: "header",
        header: "Add new file to:",
      },
      ...fileCategories.map((cat) => ({
        key: cat.display_name,
        action: () => handleCategorySelect(cat.category_key),
      })),
    ],
    [fileCategories, handleCategorySelect],
  );

  const env: Environment = caseData?.data.env ?? "live";

  const handleDownload = useCallback(
    (fileIds: string[], target: string) => {
      setDownloadTarget(target);
      downloadFiles.mutate(
        { fileIds, env: env as GetFilesEnvFilesGetEnvEnum },
        { onSettled: () => setDownloadTarget(null) },
      );
    },
    [downloadFiles, env],
  );

  const handleDownloadCategory = useCallback(
    (categoryKey: string) =>
      handleDownload(currentFileIds[categoryKey] ?? [], categoryKey),
    [currentFileIds, handleDownload],
  );

  const onFilesUploaded = useCallback(
    async (fileIds: string[], categoryKey: string) => {
      // Narrowing only. handleFileSelect already stops a drop without a Case.
      if (!caseData?.data.id) return;

      await fileCategoryOperation.mutateAsync({
        type: FILE_OPERATIONS.ATTACH_FILES,
        caseId: caseData.data.id,
        fileIds,
        categoryKey,
        currentFileIds,
      });
      setNewlyUploadedFileIds((prev) => [...prev, ...fileIds]);
    },
    [caseData?.data.id, currentFileIds, fileCategoryOperation],
  );

  const { handleDrop, isUploading } = useCaseFilesUpload({
    env,
    onFilesUploaded,
  });

  const handleFileSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Nothing starts until the Case is there, so no file uploads with nowhere
      // to go. The Files tab works again as soon as the Case arrives.
      if (!caseData?.data.id) return;

      handleDrop(acceptedFiles, rejectedFiles, pendingCategory);
      setPendingCategory(null);
    },
    [caseData?.data.id, handleDrop, pendingCategory],
  );

  const removeUploadedFileId = useCallback((fileId: string) => {
    setNewlyUploadedFileIds((prev) => prev.filter((id) => id !== fileId));
  }, []);

  const handleDeleteFile = useCallback(
    (fileId: string, fileName: string, categoryKey: string) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate(
        {
          type: FILE_OPERATIONS.DELETE,
          caseId: caseData.data.id,
          fileId,
          fileName,
          categoryKey,
          currentFileIds,
        },
        {
          onSuccess: () => removeUploadedFileId(fileId),
        },
      );
    },
    [
      caseData?.data.id,
      currentFileIds,
      fileCategoryOperation,
      removeUploadedFileId,
    ],
  );

  const handleMoveFile = useCallback(
    (
      fileId: string,
      fileName: string,
      fromCategory: string,
      toCategory: string,
    ) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate({
        type: FILE_OPERATIONS.MOVE,
        caseId: caseData.data.id,
        fileId,
        fileName,
        fromCategoryKey: fromCategory,
        toCategoryKey: toCategory,
        currentFileIds,
      });
    },
    [caseData?.data.id, currentFileIds, fileCategoryOperation],
  );

  const categoriesWithFiles = fileCategories
    .filter((cat) => cat.file_ids.length > 0)
    .map((cat) => cat.category_key);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col p-4">
        <div className="border-gray-200 border-b bg-white pb-4">
          <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
            <div className="flex gap-4">
              <div className="shrink-0">
                <Skeleton height="h-12" rounded="rounded-lg" width="w-12" />
              </div>
              <div className="flex-1 space-y-2">
                <Skeleton height="h-4" width="w-48" />
                <Skeleton height="h-3" width="w-64" />
                <div className="mt-3">
                  <Skeleton height="h-8" width="w-24" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pt-4">
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
            {times(3, (i) => (
              <div key={i} className="p-4">
                <div className="flex items-center gap-2">
                  <Skeleton height="h-4" width="w-24" />
                  <Skeleton height="h-5" rounded="rounded-full" width="w-12" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <EmptyState
        description="Case files are not available for this case"
        headline="No case data available"
        icon={faFiles}
        variant="default"
      />
    );
  }

  const buttonDisabled = !canEditFiles || isUploading;
  const uploadLabel = isUploading ? "Uploading..." : "Upload file(s)";

  return (
    <div className="flex h-full flex-col p-4">
      <div className="border-gray-200 border-b bg-white pb-4">
        <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-indigo-100">
              <Icon icon={faUpload} size="md" variant="active" />
            </div>
            <div className="flex-1">
              <h3 className="font-inter-semibold-13px text-gray-800">
                Click here to upload
              </h3>
              <p className="mt-1 font-inter-normal-12px text-gray-500">
                {canEditFiles
                  ? `Supported types: ${CASE_FILE_ACCEPTED_FORMATS.join(", ")}`
                  : "You do not have permission to upload files"}
              </p>
              <div className="mt-3 flex justify-start">
                {fileCategories.length === 1 ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    isDisabled={buttonDisabled}
                    onClick={() =>
                      handleCategorySelect(fileCategories[0].category_key)
                    }
                  >
                    {uploadLabel}
                  </Button>
                ) : (
                  <OptionsDropdown
                    buttonDataLoc="upload-file-dropdown"
                    elements={dropdownElements}
                    placement="bottom start"
                    disabled={buttonDisabled}
                  >
                    <Button
                      size="sm"
                      variant="secondary"
                      iconRight={faChevronDown}
                      isDisabled={buttonDisabled}
                    >
                      {uploadLabel}
                    </Button>
                  </OptionsDropdown>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pt-4">
        {fileCategories.length > 0 ? (
          <Accordion defaultValue={categoriesWithFiles}>
            {fileCategories.map((category) => (
              <FileCategory
                key={category.category_key}
                canDelete={canEditFiles}
                canEdit={canEditFiles}
                category={category}
                env={env}
                fileCategories={fileCategories}
                isDownloadDisabled={downloadTarget !== null}
                newlyUploadedFileIds={newlyUploadedFileIds}
                onDeleteFile={handleDeleteFile}
                onDownloadCategory={handleDownloadCategory}
                onMoveFile={handleMoveFile}
              />
            ))}
          </Accordion>
        ) : (
          <EmptyState
            description="No file categories have been configured for this case template"
            headline="No file categories"
            icon={faFiles}
            variant="default"
          />
        )}
      </div>

      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={DEFAULT_ACCEPTED_FILE_TYPES}
          disabled={!canEditFiles || isUploading}
          maxFiles={MAX_CASE_FILES}
          maxSize={MAX_FILE_SIZE_BYTES}
          noClick
          noDrag
          onDrop={handleFileSelect}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    </div>
  );
};
